import type { RoutingSkill, StageRule } from "@/lib/types";
import { stageSlugs, stageText } from "./contacts.js";

/**
 * A line's stage rules (ADR-073): who takes an inbound caller we know, by the stage their contact is
 * in. `only` reps with the rule's skills may take them, or those reps ring `first` and then anyone.
 *
 * The rules are `platform`'s: the modes, the bounds on `fallback_seconds` and one rule per stage are
 * `StageRule` in core-api's `schemas.py`, and what a rule does to a call is the ACD's
 * `src/stage_rules.py` and `src/enrichment.py`. This file only says them in a person's words and
 * shapes what the form sends.
 */

export const STAGE_RULE_MODES = ["only", "first"] as const;
export type StageRuleMode = (typeof STAGE_RULE_MODES)[number];

/** `fallback_seconds` on an `only` rule: null is "keep waiting". */
export const FALLBACK_BOUNDS = [5, 3600] as const;
/** At most five skills on one rule, as core-api allows. */
export const MAX_RULE_SKILLS = 5;

const MODE: Record<StageRuleMode, { label: string; description: string }> = {
  only: {
    label: "Only reps with",
    description: "Nobody else may take the call. The caller waits for one of them unless you set a time below.",
  },
  first: {
    label: "Reps with … first",
    description: "They ring first when one is free. When none is, anyone on the line may take the call.",
  },
};

export function stageRuleModeText(mode: string): { label: string; description: string } {
  return MODE[mode as StageRuleMode] ?? { label: mode, description: "Not a mode this console knows." };
}

/** The stages a new rule may pick: every one of the tenant's stages the line has no rule for yet,
 *  in the catalog's order. */
export function unusedStages(rules: readonly Pick<StageRule, "stage">[]): string[] {
  const taken = new Set(rules.map((r) => r.stage));
  return stageSlugs().filter((s) => !taken.has(s));
}

/** A rule for the first stage the line has none for, asking for `skill` (the catalog's first when
 *  the form has no better guess). Null when every stage has a rule. */
export function newStageRule(
  rules: readonly StageRule[],
  skill: string | undefined,
): StageRule | null {
  // The two booked stages are the ones a rule is usually for, so they come first.
  const preferred = ["consult_scheduled", "callback_scheduled"];
  const free = unusedStages(rules);
  const stage = preferred.find((s) => free.includes(s)) ?? free[0];
  if (!stage) return null;
  return {
    stage,
    mode: "only",
    skills: skill ? [{ slug: skill, min_level: 1 }] : [],
    fallback_seconds: null,
  };
}

/** Why a rule cannot be saved, or null. The form shows it beside the rule. */
export function stageRuleProblem(rule: StageRule): string | null {
  if (rule.skills.length === 0) return "Pick at least one skill.";
  if (rule.skills.length > MAX_RULE_SKILLS) return `At most ${MAX_RULE_SKILLS} skills.`;
  if (rule.mode === "only" && rule.fallback_seconds !== null) {
    const [min, max] = FALLBACK_BOUNDS;
    const s = rule.fallback_seconds;
    if (!Number.isInteger(s) || s < min || s > max) {
      return `Between ${min} and ${max.toLocaleString()} seconds, or leave it empty to keep waiting.`;
    }
  }
  return null;
}

/** A rule as it is sent: a `first` rule never waits, so it carries no fallback. */
export function stageRulePayload(rule: StageRule): StageRule {
  return {
    stage: rule.stage,
    mode: rule.mode,
    skills: rule.skills.map((s) => ({ slug: s.slug, min_level: s.min_level })),
    fallback_seconds: rule.mode === "only" ? rule.fallback_seconds : null,
    dialer: rule.dialer ?? false,
  };
}

/** One rule in a sentence, for the Lines table and the form's summary:
 *  "Consult scheduled → only Closer" or "Callback scheduled → Closer first, then anyone". */
export function describeStageRule(
  rule: StageRule,
  catalog: readonly Pick<RoutingSkill, "slug" | "name">[] = [],
): string {
  const names = rule.skills.map((s) => catalog.find((c) => c.slug === s.slug)?.name ?? s.slug);
  const who = names.length > 1 ? `${names.slice(0, -1).join(", ")} or ${names.at(-1)}` : names[0] ?? "nobody";
  const stage = stageText(rule.stage).label;
  const dials = rule.dialer ? ", inbound and from the dialer" : "";
  if (rule.mode === "first") return `${stage} → ${who} first, then anyone${dials}`;
  const after = rule.fallback_seconds ? `; anyone after ${rule.fallback_seconds} s` : "";
  return `${stage} → only ${who}${after}${dials}`;
}

/** Whether two rule lists say the same, for telling an edit from none. */
export function sameStageRules(a: readonly StageRule[], b: readonly StageRule[]): boolean {
  return JSON.stringify(a.map(stageRulePayload)) === JSON.stringify(b.map(stageRulePayload));
}
